import Table, { Column, TableRow, TableCell } from '@/components/Table';
import { Radio } from '@acast-tech/decibel';

/**
 * A reusable table component for platform selection with radio buttons.
 * Displays a list of platforms as table rows, each with a radio button for selection
 * and customizable data cells rendered via a render function.
 *
 * @template TData - The type of data associated with each platform
 * @template TRow - The type of row data associated with each platform
 *
 * @param data - The data to display in the table
 * @param selectedPlatform - The currently selected platform key
 * @param handlePlatformChange - Callback function invoked when a platform is selected via radio button
 * @param tableColumns - Array of column definitions for the table structure
 * @param renderDataCells - Function that renders the data cells for each platform row, receiving the platform's data as input
 * @param idPrefix - A prefix for the id and name of the radio buttons
 * @example
 * ```tsx
 * <PlatformSelectionTable
 *   data={platformData}
 *   selectedPlatform="Platform A"
 *   handlePlatformChange={(platform) => setSelectedPlatform(platform)}
 *   tableColumns={[
 *     { key: 'top_age_group', header: 'Top age group', align: 'left', className: 'p-2' },
 *     { key: 'median_age', header: 'Median age', align: 'right', className: 'p-2' }
 *   ]}
 *   renderDataCells={(rowData) => (
 *     <>
 *       <TableCell align="left" className="px-2">{rowData.topAgeGroup}</TableCell>
 *       <TableCell align="right" className="px-2">{rowData.medianAge}</TableCell>
 *     </>
 *   )}
 *   idPrefix="age-data"
 * />
 * ```
 *
 * @returns A table component with selectable platform rows
 *
 */

export const PlatformSelectionTable = <
  TData extends { plays: TRow[] },
  TRow extends { platform: string },
>({
  data,
  selectedPlatform,
  handlePlatformChange,
  tableColumns,
  renderDataCells,
  idPrefix,
}: {
  data: TData;
  selectedPlatform: TRow['platform'];
  handlePlatformChange: (platform: TRow['platform']) => void;
  tableColumns: Column[];
  renderDataCells: (rowData: TRow) => React.ReactNode;
  idPrefix: string;
}) => {
  return (
    <Table columns={tableColumns} className="border-collapse  max-w-full">
      {data.plays?.length > 0 &&
        data.plays.map((rowData) => (
          <TableRow
            key={`${idPrefix}-${rowData.platform}`}
            className={`${selectedPlatform === rowData.platform ? 'bg-[#F8F8FC]' : ''}`}
          >
            <TableCell align="left" className="px-2">
              <Radio
                value={rowData.platform}
                name={`${idPrefix}-${rowData.platform}`}
                id={`${idPrefix}-${rowData.platform}`}
                onChange={() => handlePlatformChange(rowData.platform)}
                checked={selectedPlatform === rowData.platform}
              />
            </TableCell>
            <TableCell className="text-textDarkGrey font-medium px-2">{rowData.platform}</TableCell>
            {renderDataCells(rowData)}
          </TableRow>
        ))}
    </Table>
  );
};
